import React from 'react';

import AccountIcon from './builders/iconsData/navigationIcon/Account';
import AlertsIcon from './builders/iconsData/navigationIcon/Alerts';
import CommunityIcon from './builders/iconsData/navigationIcon/Community';
import ConductorIcon from './builders/iconsData/navigationIcon/Conductor';
import ContentIcon from './builders/iconsData/navigationIcon/Content';
import DashboardIcon from './builders/iconsData/navigationIcon/Dashboard';
import ExclusionListIcon from './builders/iconsData/navigationIcon/ExclusionList';
import HelpIcon from './builders/iconsData/navigationIcon/Help';
import HomeIcon from './builders/iconsData/navigationIcon/Home';
import IntegrationsIcon from './builders/iconsData/navigationIcon/Integrations';
import IssuesIcon from './builders/iconsData/navigationIcon/Issues';
import LockIcon from './builders/iconsData/navigationIcon/Lock';
import LogoutIcon from './builders/iconsData/navigationIcon/Logout';
import MagicWandIcon from './builders/iconsData/navigationIcon/MagicWand';
import NotificationsIcon from './builders/iconsData/navigationIcon/Notifications';
import PagesIcon from './builders/iconsData/navigationIcon/Pages';
import PerformanceIcon from './builders/iconsData/navigationIcon/Performance';
import PlatformIcon from './builders/iconsData/navigationIcon/Platform';
import ProfileIcon from './builders/iconsData/navigationIcon/Profile';
import SearchIcon from './builders/iconsData/navigationIcon/Search';
import SettingsIcon from './builders/iconsData/navigationIcon/Settings';
import SupportIcon from './builders/iconsData/navigationIcon/Support';
import SwitchAccountIcon from './builders/iconsData/navigationIcon/SwitchAccount';
import TechnicalIcon from './builders/iconsData/navigationIcon/Technical';
import TeamIcon from './builders/iconsData/navigationIcon/Team';
import ToggleAdminIcon from './builders/iconsData/navigationIcon/ToggleAdmin';
import TrashIcon from './builders/iconsData/navigationIcon/Trash';
import WebsitesIcon from './builders/iconsData/navigationIcon/Websites';
import WorkspaceIcon from './builders/iconsData/navigationIcon/Workspace';

import InlineIcon from './builders/InlineIcon.part';

import NavigationLayoutContext from '~/contexts/NavigationLayoutContext';

import type IconBuilder from './builders/iconsData/IconBuilder';

import {
	type Merge,
} from '~/types/utilities';



export enum NavigationIconType {
	Account = 'account',
	Alerts = 'alerts',
	Community = 'community',
	Conductor = 'conductor',
	Content = 'content',
	Dashboard = 'dashboard',
	ExclusionList = 'exclusion-list',
	Help = 'help',
	Home = 'home',
	Integrations = 'integrations',
	Issues = 'issues',
	Lock = 'lock',
	Logout = 'logout',
	MagicWand = 'magic-wand',
	Notifications = 'notifications',
	Pages = 'pages',
	Performance = 'performance',
	Platform = 'platform',
	Profile = 'profile',
	Search = 'search',
	Settings = 'settings',
	Support = 'support',
	SwitchAccount = 'switch-account',
	Technical = 'technical',
	Team = 'team',
	ToggleAdmin = 'toggle-admin',
	Trash = 'trash',
	Websites = 'websites',
	Workspace = 'workspace',
}

const componentMapping = {
	[NavigationIconType.Account]: AccountIcon,
	[NavigationIconType.Alerts]: AlertsIcon,
	[NavigationIconType.Community]: CommunityIcon,
	[NavigationIconType.Conductor]: ConductorIcon,
	[NavigationIconType.Content]: ContentIcon,
	[NavigationIconType.Dashboard]: DashboardIcon,
	[NavigationIconType.ExclusionList]: ExclusionListIcon,
	[NavigationIconType.Help]: HelpIcon,
	[NavigationIconType.Home]: HomeIcon,
	[NavigationIconType.Integrations]: IntegrationsIcon,
	[NavigationIconType.Issues]: IssuesIcon,
	[NavigationIconType.Lock]: LockIcon,
	[NavigationIconType.Logout]: LogoutIcon,
	[NavigationIconType.MagicWand]: MagicWandIcon,
	[NavigationIconType.Notifications]: NotificationsIcon,
	[NavigationIconType.Pages]: PagesIcon,
	[NavigationIconType.Performance]: PerformanceIcon,
	[NavigationIconType.Platform]: PlatformIcon,
	[NavigationIconType.Profile]: ProfileIcon,
	[NavigationIconType.Search]: SearchIcon,
	[NavigationIconType.Settings]: SettingsIcon,
	[NavigationIconType.Support]: SupportIcon,
	[NavigationIconType.SwitchAccount]: SwitchAccountIcon,
	[NavigationIconType.Technical]: TechnicalIcon,
	[NavigationIconType.Team]: TeamIcon,
	[NavigationIconType.ToggleAdmin]: ToggleAdminIcon,
	[NavigationIconType.Trash]: TrashIcon,
	[NavigationIconType.Websites]: WebsitesIcon,
	[NavigationIconType.Workspace]: WorkspaceIcon,
} as const;



type BaseProps<IconType extends NavigationIconType = NavigationIconType> = {
	/** Custom icon color */
	color?: string,
	/** Square size of icon */
	size?: number,
	/** Type of icon */
	type: IconType,
};

type IconProps<IconType extends NavigationIconType = NavigationIconType> = (
	Parameters<typeof componentMapping[IconType]['icon']>[0]
);

type Props<IconType extends NavigationIconType = NavigationIconType> = (
	Merge<IconProps<IconType>, BaseProps<IconType>>
);

// Icon data for OLD navigation (original icons - before cond-ui-kit)
// These use direct SVG paths, not cond-ui-kit icons
const oldNavigationIcons: Partial<Record<NavigationIconType, IconBuilder>> = {
	[NavigationIconType.Settings]: {
		icon: ({ color = '#ffffff' }) => (
			<g id="cog_wheel">
				<path
					d="M12.941,15.524A2.524,2.524,0,1,1,15.506,13,2.577,2.577,0,0,1,12.941,15.524Z"
					fill="none"
					stroke={color}
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="2"
				/>
				<path
					d="M19.74,13.95a6.723,6.723,0,0,0,0-1.809L21.671,10.6a.46.46,0,0,0,.091-.543L19.924,6.894c-.083-.142-.367-.166-.551-.091l-2.228.926a8.138,8.138,0,0,0-1.612-.941l-.387-2.336c0-.155-.184-.452-.368-.452H11.1c-.184,0-.461.2-.461.362l-.338,2.417a9.626,9.626,0,0,0-1.591.929L6.509,6.8a.622.622,0,0,0-.644.182L4.028,10.151a.508.508,0,0,0,.183.543l1.943,1.449a7.2,7.2,0,0,0,.011,1.826L4.211,15.487a.423.423,0,0,0-.091.543L5.957,19.2c.094.157.4.158.552.089l2.28-.871a8.158,8.158,0,0,0,1.567.911l.38,2.313c0,.18.27.361.459.361h3.676c.183,0,.458-.185.458-.361l.369-2.353a8.1,8.1,0,0,0,1.535-.932l2.324.932a.54.54,0,0,0,.552-.181l1.837-3.165a.443.443,0,0,0-.092-.543Z"
					fill="none"
					stroke={color}
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="2"
				/>
			</g>
		),
		viewBoxHeight: 26,
		viewBoxWidth: 26,
	},
	[NavigationIconType.Account]: {
		icon: ({ color = '#ffffff' }) => {
			return (
				<g transform="translate(-18.000000, -780.000000)">
					<g
						id="User-menu"
						transform="translate(0.000000, 766.000000)"
					>
						<g
							id="Teams-Copy"
							transform="translate(0.000000, 4.000000)"
						>
							<g
								id="Group-7"
								transform="translate(19.000000, 11.000000)"
							>
								<polyline
									fill="none"
									id="Stroke-1"
									points="5.9,17.3 1.1,17.3 1.1,4.6 17.3,4.6 17.3,6.6"
									stroke={color}
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
								/>
								<polyline
									fill="none"
									id="Stroke-3"
									points="5.7,4.6 5.7,0.8 12.7,0.8 12.7,4.6"
									stroke={color}
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
								/>
								<path
									d="M15.6,18.2c-1-0.2-1.7-1.2-1.5-2.2c0.2-1,1.2-1.7,2.3-1.5c1,0.2,1.7,1.2,1.5,2.2
								C17.7,17.7,16.6,18.4,15.6,18.2z M20.8,18c0.2-0.6,0.2-0.7,0.3-1.3l1.6-0.8c0.1-0.1,0.2-0.3,0.1-0.4L22,12.9
								c0-0.1-0.2-0.2-0.4-0.1l-1.8,0.4c-0.5-0.5-0.5-0.5-1-0.9l0.1-1.8c0-0.1-0.1-0.4-0.2-0.4l-2.7-0.5c-0.1,0-0.4,0.1-0.4,0.2
								l-0.6,1.7c-0.7,0.2-0.7,0.2-1.3,0.5l-1.5-1c-0.1-0.1-0.4-0.1-0.5,0l-1.8,2.1c-0.1,0.1,0,0.3,0.1,0.4l1.2,1.3
								c-0.2,0.6-0.2,0.7-0.3,1.3l-1.7,0.8C9,17,9,17.2,9.1,17.3l0.9,2.6c0,0.1,0.3,0.2,0.4,0.1l1.8-0.3c0.4,0.4,0.5,0.5,1,0.9
								l-0.1,1.8c0,0.1,0.1,0.3,0.3,0.3l2.7,0.5c0.1,0,0.4-0.1,0.4-0.2l0.6-1.7c0.7-0.2,0.7-0.2,1.3-0.5l1.6,1c0.1,0.1,0.4,0,0.4-0.1
								l1.8-2.1c0.1-0.1,0.1-0.3,0-0.4L20.8,18z"
									fill="none"
									id="Stroke-5"
									stroke={color}
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
								/>
							</g>
						</g>
					</g>
				</g>
			);
		},
		viewBoxHeight: 26,
		viewBoxWidth: 26,
	},
	[NavigationIconType.Community]: {
		icon: ({ color = '#ffffff' }) => {
			return (
				<>
					<g transform="scale(0.9) translate(51.2, 51.2)">
						<path
							d="M769.078 112c93.47 0 169.237 76.352 169.237 170.539 0 46.414-18.43 88.467-48.28 119.216 79.766 43.411 133.963 128.419 133.965 226.179v21.598c-0.003 22.65-18.226 41.011-40.701 41.014-22.478 0-40.698-18.363-40.701-41.014v-21.598c-0.003-96.568-77.704-174.856-173.536-174.856-92.064 0.003-167.349 72.262-173.126 163.568 53.072 60.526 85.293 140.054 85.293 227.174v43.165c0 22.653-18.222 41.014-40.701 41.014-22.477-0.003-40.701-18.363-40.701-41.014v-43.165c0-144.261-116.048-261.213-259.205-261.216-143.16 0-259.221 116.954-259.221 261.216v43.165c0 22.653-18.221 41.014-40.701 41.014s-40.701-18.362-40.701-41.014v-43.165c0-139.693 82.815-259.891 201.669-313.472-44.8-39.181-73.129-96.981-73.133-161.448 0-118.034 94.956-213.72 212.087-213.72 117.126 0.003 212.070 95.688 212.070 213.72-0.003 64.472-28.344 122.267-73.149 161.448 15.682 7.067 30.744 15.275 45.064 24.555 19.402-66.050 64.434-121.005 123.498-153.149-29.845-30.749-48.262-72.806-48.262-119.216 0-94.187 75.765-170.539 169.235-170.539zM340.622 237.21c-72.171 0-130.685 58.963-130.685 131.691 0.006 72.718 58.518 131.675 130.685 131.675 72.163-0.003 130.662-58.958 130.669-131.675 0-72.726-58.502-131.69-130.669-131.691zM769.078 194.029c-48.51 0-87.834 39.629-87.834 88.51 0 48.883 39.323 88.512 87.834 88.512s87.835-39.629 87.835-88.512c0-48.882-39.325-88.51-87.835-88.51z"
							fill={color}
							stroke="inherit"
						>
						</path>
					</g>
				</>
			);
		},
		viewBoxHeight: 1024,
		viewBoxWidth: 1024,
	},
};

const NavigationIcon: React.FC<Props> = (props) => {
	const {
		color,
		size = 26,
		type,
		...rest
	} = props;

	// Check navigation layout context to determine which icon set to use
	// For Settings, Community, and Account, use different icons for old vs new navigation
	let iconBuilder = componentMapping[type];

	// Call hook unconditionally (React hooks rules)
	// Use React.useContext directly to avoid throwing errors
	const contextValue = React.useContext(NavigationLayoutContext);
	const currentLayout = contextValue?.currentLayout;

	if (contextValue && currentLayout !== undefined) {
		const isNewNavigation = currentLayout === 'new';

		// For old navigation, use original icons; for new navigation, use cond-ui-kit icons
		// Settings, Community, and Account icons were updated to cond-ui-kit for new navigation
		// but should use original icons for old navigation
		if (!isNewNavigation && oldNavigationIcons[type]) {
			iconBuilder = oldNavigationIcons[type];
		}
	}

	const icon = iconBuilder.icon({
		color,
		size,
		...rest,
	});

	return (
		<InlineIcon
			icon={icon}
			size={size}
			viewBoxHeight={iconBuilder.viewBoxHeight}
			viewBoxWidth={iconBuilder.viewBoxWidth}
		/>
	);
};



export default NavigationIcon;
